import { CONFIG_TRUSTARC_DOMAIN } from '@src/config/env'
import { removeProtocol } from '@src/quince-site-common/src/utils/urlUtils'
import { executeFireAndForgotTask } from '@src/utils/fire-and-forgot-task'

import { initialiseTrustARCPromise, isTrustARCScriptLoading, TRUSTARC_CONFIG } from './TrustArc'
import { TRUSTE_CONSENT, TRUSTE_CONSENT_DECISION, TTruste } from './trustarc.type'

declare global {
  interface Window {
    truste: TTruste
  }
}

class TrustARC {
  private resolveTrustArcFeatureEnablePromise: (value: boolean) => void = () => null
  private isTrustARCFeatureEnablePromise = new Promise<boolean>(resolve => {
    this.resolveTrustArcFeatureEnablePromise = resolve
  })

  // Check if Truste ARC feature is enabled
  private async isTrustARCFeatureEnable() {
    const isFeatureEnable = await this.isTrustARCFeatureEnablePromise
    return isFeatureEnable
  }

  async init() {
    // Check if Truste ARC feature is enabled
    const isFeatureEnable = await this.isTrustARCFeatureEnable()

    // Check if Truste ARC script is enabled and feature is enabled
    if (!TRUSTARC_CONFIG.config.enabled || !isFeatureEnable) return

    // Initialize Truste ARC script
    executeFireAndForgotTask(async () => {
      if (TRUSTARC_CONFIG.initialize) {
        await TRUSTARC_CONFIG.initialize()
      }
    }, TRUSTARC_CONFIG.config.eventIdealTimeout)
  }

  hasAllowedAdvertisingTrackerConsent() {
    if (typeof window.truste.cma === 'undefined') return true // TrustARC feature is not enabled

    const { consentDecision } = window.truste.cma.callApi<{ consentDecision: TRUSTE_CONSENT_DECISION[] }>(
      'getGDPRConsentDecision',
      CONFIG_TRUSTARC_DOMAIN,
    )

    return (
      consentDecision.includes(TRUSTE_CONSENT_DECISION.ADVERTISING) ||
      consentDecision.includes(TRUSTE_CONSENT_DECISION.ALL)
    )
  }

  // Open Truste CCM Popup
  handleOpenTrusteCCMPopup() {
    window.truste.eu.clickListener()
  }

  async checkTrusteCCMConsent(domain: string) {
    const isFeatureEnable = await this.isTrustARCFeatureEnable()
    if (!isFeatureEnable) return true

    // Check if Truste ARC script is loaded
    await this.hasTrusteArcScriptLoaded()

    // Check Truste CCM Consent
    const consent = this.checkTrusteCCMConsentFromAPI(removeProtocol(domain))

    return consent === TRUSTE_CONSENT.APPROVED
  }

  // get Truste ARC feature enable promise
  getReolverTrustArcFeatureEnablePromise() {
    return this.resolveTrustArcFeatureEnablePromise
  }

  // Check if Truste ARC window object is loaded
  private checkTrusteCCMAPILoaded() {
    return new Promise(resolve => {
      const interval = setInterval(() => {
        if (typeof window.truste.cma !== 'undefined') {
          clearInterval(interval)
          resolve(true)
        }
      }, 50)
    })
  }

  // Check if Truste ARC script is loaded
  private async hasTrusteArcScriptLoaded() {
    if (isTrustARCScriptLoading) {
      // Wait for Truste ARC script to load
      await initialiseTrustARCPromise
      await this.checkTrusteCCMAPILoaded() // It takes sometime to load truste API in window
    }
    return true
  }

  // Check Truste CCM Consent from Truste API
  private checkTrusteCCMConsentFromAPI(domain: string): TRUSTE_CONSENT {
    const currentDomain = window.location.host

    const res = window.truste.cma.callApi<{ consent: TRUSTE_CONSENT }>(
      'getConsent',
      currentDomain,
      domain,
      currentDomain,
    )

    // If domain is not present in Truste CCM, then return denied if advertising is not allowed
    return typeof res.consent !== 'undefined' ? res.consent : TRUSTE_CONSENT.DENIED
  }
}

const TrustArcCl = new TrustARC()

export default TrustArcCl
